import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';

/**
 * Re-enable nested card-gallery slideshows in vitrine carousels on desktop only (mobile-disabled keeps mobile off).
 *
 * @param {import('slideshow').Slideshow | null | undefined} slideshow
 */
function syncResourceListCardGallery(slideshow) {
  if (!slideshow) return;
  if (slideshow.dataset.settingDisabled === 'true') return;
  if (!slideshow.closest('.resource-list__carousel')) return;

  slideshow.setAttribute('mobile-disabled', '');
  slideshow.disabled = false;
}

/**
 * @param {ParentNode} [root]
 */
function syncAllResourceListCardGalleries(root = document) {
  root.querySelectorAll('.resource-list__carousel .card-gallery slideshow-component').forEach((slideshow) => {
    syncResourceListCardGallery(slideshow);
  });
}

// MOXIE: el burst inicial comparte un doble-rAF; cards tardías (infinite scroll, morph) agendan el suyo.
let initialSyncDone = false;

function scheduleSyncResourceListCardGalleries(root = document) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      syncAllResourceListCardGalleries(root);
      initialSyncDone = true;
    });
  });
}

/**
 * Mobile gallery progress bar synced to slideshow:select.
 */
class CustomPdpGalleryProgress extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#fill = this.refs.fill;
    this.#gallery = this.closest('.custom-pdp-gallery, .card-gallery');
    this.#slideshow = this.#gallery?.querySelector('slideshow-component') ?? null;
    this.#total = this.#getTotal();

    this.#update(0);
    this.#gallery?.addEventListener(SlideshowSelectEvent.eventName, this.#onSelect);
    if (initialSyncDone) {
      scheduleSyncResourceListCardGalleries(this.#gallery ?? document);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#gallery?.removeEventListener(SlideshowSelectEvent.eventName, this.#onSelect);
  }

  /** @param {import('@theme/events').SlideshowSelectEvent} event */
  #onSelect = (event) => {
    this.#update(event.detail?.index ?? 0);
  };

  /** @param {number} index */
  #update(index) {
    if (!this.#fill) return;
    const safeIndex = Math.min(Math.max(index, 0), this.#total - 1);
    const width = ((safeIndex + 1) / this.#total) * 100;
    this.#fill.style.width = `${width}%`;
  }

  #getTotal() {
    if (this.#gallery?.classList.contains('card-gallery')) {
      return Math.max(1, this.#slideshow?.querySelectorAll('slideshow-slide:not([hidden])').length ?? 1);
    }

    return Math.max(1, Number(this.dataset.total) || 1);
  }

  /** @type {HTMLElement | null} */
  #fill = null;
  /** @type {number} */
  #total = 1;
  /** @type {HTMLElement | null} */
  #gallery = null;
  /** @type {import('slideshow').Slideshow | null} */
  #slideshow = null;
}

if (!customElements.get('custom-pdp-gallery-progress')) {
  customElements.define('custom-pdp-gallery-progress', CustomPdpGalleryProgress);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => scheduleSyncResourceListCardGalleries());
} else {
  scheduleSyncResourceListCardGalleries();
}
